import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { User, Gift, Mail, Lock, PartyPopper, Edit2, LogOut } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle, DrawerDescription, DrawerFooter } from '@/components/ui/drawer';
import { toast } from 'sonner';
import { authAPI, customerAPI, promotionAPI, rewardAPI } from '@/services/api';
import LoyaltyPath from '@/components/LoyaltyPath';
import PromoCarousel from '@/components/PromoCarousel';

// Countdown Timer Component
const CountdownTimer = ({ expiry, onExpire }) => {
  const [timeLeft, setTimeLeft] = useState('');
  
  useEffect(() => {
    const timer = setInterval(() => {
      const now = new Date();
      const diff = expiry - now;
      
      if (diff <= 0) {
        clearInterval(timer);
        onExpire();
        return;
      }
      
      const minutes = Math.floor(diff / 60000);
      const seconds = Math.floor((diff % 60000) / 1000);
      setTimeLeft(`${minutes}:${seconds.toString().padStart(2, '0')}`);
    }, 1000);
    
    return () => clearInterval(timer);
  }, [expiry, onExpire]);
  
  return (
    <p className="text-lg font-bold text-amber-600 mt-2">
      Expira en: {timeLeft}
    </p>
  );
};

const CustomerApp = () => {
  const navigate = useNavigate();
  const [customer, setCustomer] = useState(() => {
    const saved = localStorage.getItem('darkar_customer');
    return saved ? JSON.parse(saved) : null;
  });
  const [promotions, setPromotions] = useState([]);
  const [rewards, setRewards] = useState([]);
  const [showAuth, setShowAuth] = useState(false);
  const [authMode, setAuthMode] = useState('login');
  const [showReward, setShowReward] = useState(false);
  const [showEditName, setShowEditName] = useState(false);
  const [showAvatarPicker, setShowAvatarPicker] = useState(false);
  const [showRedemptionCode, setShowRedemptionCode] = useState(false);
  const [redemptionCode, setRedemptionCode] = useState(null);
  const [codeExpiry, setCodeExpiry] = useState(null);
  const [authData, setAuthData] = useState({ email: '', password: '', confirmPassword: '', name: '', phone: '' });
  const [editName, setEditName] = useState('');
  const [loading, setLoading] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isInstalled, setIsInstalled] = useState(false);
  
  // Password Reset States
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [resetToken, setResetToken] = useState('');
  const [resetData, setResetData] = useState({ newPassword: '', confirmPassword: '' });
  const [resetEmail, setResetEmail] = useState('');
  
  // Avatar options
  const avatarOptions = [
    'https://hosting.photobucket.com/d970c25b-dfba-4164-a867-030817bbaa89/e448fd25-1722-4ceb-a3ae-4dc20d2104ca.png',
    'https://hosting.photobucket.com/d970c25b-dfba-4164-a867-030817bbaa89/d0c85465-d60a-4243-b2c2-fa5fdca478f9.png',
    'https://hosting.photobucket.com/d970c25b-dfba-4164-a867-030817bbaa89/2d2bbd80-2af1-46de-abf1-5befa6fc5188.png',
    'https://hosting.photobucket.com/d970c25b-dfba-4164-a867-030817bbaa89/3546e933-036f-4b87-a93e-6eabf0c94569.png',
    'https://hosting.photobucket.com/d970c25b-dfba-4164-a867-030817bbaa89/c519b22f-ce64-4fc7-9b2c-ea6714ce095e.png',
    'https://hosting.photobucket.com/d970c25b-dfba-4164-a867-030817bbaa89/232243c3-546f-4238-9059-c00e01fbe891.png',
    'https://hosting.photobucket.com/d970c25b-dfba-4164-a867-030817bbaa89/7ef9b994-63a9-4daa-9fc3-b943078cf5db.png',
    'https://hosting.photobucket.com/d970c25b-dfba-4164-a867-030817bbaa89/a2394f1f-db23-4cbc-9f76-32c373d1f898.png',
    'https://hosting.photobucket.com/d970c25b-dfba-4164-a867-030817bbaa89/9dbbc086-3c91-446d-8f04-d8acd595316b.png',
    'https://hosting.photobucket.com/d970c25b-dfba-4164-a867-030817bbaa89/6ccf5ab3-428e-4e46-a808-88ade05e1a76.png',
    'https://hosting.photobucket.com/d970c25b-dfba-4164-a867-030817bbaa89/923966bf-4aba-4a7e-b3f8-c571a331ed29.png',
  ];
  
  // Secret admin access - tap logo 5 times
  const logoClickCount = useRef(0);
  const logoClickTimer = useRef(null);
  
  const handleLogoClick = () => {
    logoClickCount.current += 1;
    
    if (logoClickTimer.current) {
      clearTimeout(logoClickTimer.current);
    }
    
    if (logoClickCount.current >= 5) {
      logoClickCount.current = 0;
      navigate('/admin');
    } else {
      logoClickTimer.current = setTimeout(() => {
        logoClickCount.current = 0;
      }, 2000);
    }
  };

  // Check if app is installed
  useEffect(() => {
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setIsInstalled(true);
    }
    
    const handleBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    
    window.addEventListener('beforeinstallprompt', handleBeforeInstall);
    window.addEventListener('appinstalled', () => {
      setIsInstalled(true);
      setDeferredPrompt(null);
      toast.success('¡App instalada correctamente!');
    });
    
    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
    };
  }, []);

  // Check for password reset token in URL
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get('reset_token');
    
    if (token) {
      // Verify token and show reset form
      const verifyToken = async () => {
        try {
          const response = await authAPI.verifyResetToken(token);
          if (response.data.valid) {
            setResetToken(token);
            setResetEmail(response.data.email);
            setShowResetPassword(true);
            // Clean URL
            window.history.replaceState({}, document.title, window.location.pathname);
          } else {
            toast.error(response.data.message || 'Enlace inválido o expirado');
            window.history.replaceState({}, document.title, window.location.pathname);
          }
        } catch (error) {
          toast.error('Enlace inválido o expirado');
          window.history.replaceState({}, document.title, window.location.pathname);
        }
      };
      verifyToken();
    }
  }, []);

  const handleInstallClick = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        setDeferredPrompt(null);
      }
    } else {
      toast.info(
        'Para instalar: Abre el menú de tu navegador y selecciona "Agregar a pantalla de inicio"',
        { duration: 5000 }
      );
    }
  };

  useEffect(() => {
    if (customer) {
      localStorage.setItem('darkar_customer', JSON.stringify(customer));
    } else {
      localStorage.removeItem('darkar_customer');
    }
  }, [customer]);

  const loadPromotions = useCallback(async () => {
    try {
      const response = await promotionAPI.list(true);
      setPromotions(response.data);
    } catch (error) {
      console.error('Error loading promotions:', error);
    }
  }, []);

  const loadRewards = useCallback(async () => {
    try {
      const response = await rewardAPI.list(true);
      setRewards(response.data);
    } catch (error) {
      console.error('Error loading rewards:', error);
    }
  }, []);

  const refreshCustomer = useCallback(async () => {
    if (!customer?.id) return;
    try {
      const response = await customerAPI.get(customer.id);
      if (response.data) {
        setCustomer(response.data);
      }
    } catch (error) {
      // Silent fail - customer might have been deleted
      console.error('Error refreshing customer:', error);
    }
  }, [customer?.id]);

  useEffect(() => {
    loadPromotions();
    loadRewards();
  }, [loadPromotions, loadRewards]);

  useEffect(() => {
    if (customer?.id) {
      refreshCustomer(); // Refresh immediately on mount
      const interval = setInterval(refreshCustomer, 30000);
      return () => clearInterval(interval);
    }
  }, [customer?.id, refreshCustomer]);

  const handleLogin = async () => {
    if (!authData.email || !authData.password) {
      toast.error('Ingresa correo y contraseña');
      return;
    }

    setLoading(true);
    try {
      const response = await authAPI.login({
        email: authData.email.trim().toLowerCase(),
        password: authData.password
      });
      
      if (response.data.success) {
        setCustomer(response.data.customer);
        localStorage.setItem('darkar_pass', authData.password);
        setShowAuth(false);
        setAuthData({ email: '', password: '', confirmPassword: '', name: '', phone: '' });
        toast.success(response.data.message);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al iniciar sesión');
    } finally {
      setLoading(false);
    }
  };

  // Password Reset Handlers
  const handleForgotPassword = async () => {
    if (!forgotEmail || !forgotEmail.includes('@')) {
      toast.error('Ingresa un correo electrónico válido');
      return;
    }

    setLoading(true);
    try {
      const response = await authAPI.requestPasswordReset(forgotEmail.trim().toLowerCase());
      if (response.data.success) {
        toast.success('Si el correo existe, recibirás instrucciones en tu bandeja de entrada');
        setShowForgotPassword(false);
        setForgotEmail('');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al procesar la solicitud');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async () => {
    if (!resetData.newPassword || resetData.newPassword.length < 4) {
      toast.error('La contraseña debe tener al menos 4 caracteres');
      return;
    }
    if (resetData.newPassword !== resetData.confirmPassword) {
      toast.error('Las contraseñas no coinciden');
      return;
    }

    setLoading(true);
    try {
      const response = await authAPI.resetPassword(resetToken, resetData.newPassword);
      if (response.data.success) {
        toast.success('¡Contraseña actualizada! Ya puedes iniciar sesión.');
        setShowResetPassword(false);
        setResetToken('');
        setResetData({ newPassword: '', confirmPassword: '' });
        setResetEmail('');
        // Open login drawer
        setShowAuth(true);
        setAuthMode('login');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al actualizar la contraseña');
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async () => {
    if (!authData.name || authData.name.trim().length < 2) {
      toast.error('Ingresa tu nombre');
      return;
    }
    if (!authData.email || !authData.password) {
      toast.error('Ingresa correo y contraseña');
      return;
    }
    if (authData.password.length < 4) {
      toast.error('La contraseña debe tener al menos 4 caracteres');
      return;
    }
    if (authData.password !== authData.confirmPassword) {
      toast.error('Las contraseñas no coinciden');
      return;
    }
    if (!authData.phone || authData.phone.trim().length < 8) {
      toast.error('Ingresa un número de teléfono válido');
      return;
    }

    setLoading(true);
    try {
      const response = await authAPI.register({
        email: authData.email.trim().toLowerCase(),
        password: authData.password,
        name: authData.name || '',
        phone: authData.phone.trim()
      });
      
      if (response.data.success) {
        setCustomer(response.data.customer);
        localStorage.setItem('darkar_pass', authData.password);
        setShowAuth(false);
        setAuthData({ email: '', password: '', confirmPassword: '', name: '', phone: '' });
        toast.success('¡Bienvenido a DARKAR CO.!');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al registrarse');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateName = async () => {
    if (!editName.trim()) {
      toast.error('Ingresa tu nombre');
      return;
    }

    setLoading(true);
    try {
      const response = await authAPI.updateProfile(customer.id, { name: editName.trim() });
      if (response.data.success) {
        setCustomer(response.data.customer);
        setShowEditName(false);
        toast.success('Nombre actualizado');
      }
    } catch (error) {
      toast.error('Error al actualizar nombre');
    } finally {
      setLoading(false);
    }
  };

  const handleRedeemReward = async (reward) => {
    if (!customer) return;

    setLoading(true);
    try {
      const response = await rewardAPI.requestRedemption(customer.id, reward.id, 'reward');
      if (response.data.success) {
        setRedemptionCode({
          code: response.data.code,
          rewardName: response.data.reward_name,
          cost: response.data.cost,
          currencyType: response.data.currency_type
        });
        setCodeExpiry(new Date(response.data.expires_at));
        setShowReward(false);
        setShowRedemptionCode(true);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al solicitar canje');
    } finally {
      setLoading(false);
    }
  };

  const handleConvertStickers = async () => {
    if (!customer) return;

    // Show confirmation dialog
    const confirmed = window.confirm('¿Quieres cambiar tus 10 stickers por una moneda de oro?');
    if (!confirmed) return;

    setLoading(true);
    try {
      const response = await rewardAPI.convertStickers(customer.id);
      if (response.data.success) {
        toast.success(response.data.message, { icon: '🪙' });
        refreshCustomer();
        setShowReward(false);
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al convertir stickers');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelRedemption = async () => {
    if (!customer) return;
    try {
      await rewardAPI.cancelRedemption(customer.id);
    } catch (error) {
      // Ignore errors on cancel
    }
    setShowRedemptionCode(false);
    setRedemptionCode(null);
    setCodeExpiry(null);
    refreshCustomer();
  };

  const handleLogout = () => {
    setCustomer(null);
    localStorage.removeItem('darkar_customer');
    localStorage.removeItem('darkar_pass');
    toast.info('¡Hasta pronto!');
  };

  const handleSelectAvatar = async (avatarUrl) => {
    if (!customer) return;
    
    try {
      await authAPI.updateProfile(customer.id, { avatar: avatarUrl });
      setCustomer(prev => ({ ...prev, avatar: avatarUrl }));
      setShowAvatarPicker(false);
      toast.success('¡Avatar actualizado!');
    } catch (error) {
      toast.error('Error al actualizar avatar');
    }
  };

  // Calculate stickers and coins
  // Stickers are now a direct field - no more complex calculations!
  const currentStickers = customer ? (customer.stickers || 0) : 0;
  const goldCoins = customer ? (customer.gold_coins || 0) : 0;
  const canConvertToCoins = customer && currentStickers >= 10 && goldCoins < 5;
  const hasStickersToRedeem = customer && currentStickers > 0;
  const hasCoinsToRedeem = goldCoins > 0;
  const canRedeem = hasStickersToRedeem || hasCoinsToRedeem || canConvertToCoins;

  return (
    <div 
      className={`min-h-screen ${!customer ? 'animated-gradient' : ''}`}
      style={customer ? {
        backgroundImage: 'url(https://customer-assets-jai6qajn.emergentagent.net/job_d5d032d0-152d-421b-affb-485dab137e9a/artifacts/mdl65ajp_animationx500.gif)',
        backgroundRepeat: 'repeat',
        backgroundSize: 'auto'
      } : {}}
    >
      {/* Header */}
      <header className="sticky top-0 z-40 shadow-lg header-stripes">
        <div className="max-w-lg mx-auto px-4 py-3 flex items-center justify-center relative">
          <img 
            src="https://hosting.photobucket.com/d970c25b-dfba-4164-a867-030817bbaa89/6f6d5330-8fb2-46d0-894a-7b94789a39b6.png" 
            alt="Logo" 
            className="h-14 object-contain cursor-pointer select-none"
            onClick={handleLogoClick}
          />
          
          {customer && (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleLogout}
              className="absolute right-4 text-white hover:text-rose-dark hover:bg-white/20"
              data-testid="logout-btn"
            >
              <LogOut className="w-5 h-5" />
            </Button>
          )}
        </div>
      </header>

      <main className="max-w-lg mx-auto px-4 py-6 pb-32">
        {customer ? (
          <>
            {/* Loyalty Path - First thing visible */}
            <div className="mb-6">
              <h1 
                className="text-center text-white text-5xl mb-4"
                style={{ 
                  fontFamily: "'MeltSwashes', 'Fredoka', sans-serif",
                  textShadow: '3px 3px 6px rgba(0,0,0,0.4)',
                  letterSpacing: '2px'
                }}
              >
                Isla Mecono
              </h1>
              <LoyaltyPath currentStep={Math.min(currentStickers, 10)} />
            </div>

            {/* Rewards Banner */}
            <div 
              onClick={() => setShowReward(true)}
              className="w-full rounded-3xl cursor-pointer hover:scale-[1.02] transition-transform duration-300 overflow-hidden mb-4"
              style={{ border: '5px solid #FFA6A6' }}
              data-testid="rewards-banner"
            >
              <img 
                src="https://hosting.photobucket.com/d970c25b-dfba-4164-a867-030817bbaa89/cc57b1d1-b837-4963-b730-08fa4506dd36.png"
                alt="Ver Premios Disponibles"
                className="w-full h-auto"
              />
            </div>

            {/* WiFi Banner */}
            <img 
              src="https://hosting.photobucket.com/d970c25b-dfba-4164-a867-030817bbaa89/7dfd158b-1376-4ec6-9cc8-84fbe668f24f.png"
              alt="Conéctate al WiFi"
              className="w-full rounded-3xl mb-4"
              style={{ border: '5px solid #FFA6A6' }}
            />

            {/* Customer Card */}
            <Card className="mb-6 rounded-3xl overflow-hidden bg-white shadow-soft-pop" style={{ border: '5px solid #FFA6A6' }} data-testid="customer-card">
              <CardContent className="p-6">
                <div className="flex items-center gap-4 mb-4">
                  <div 
                    className="relative w-14 h-14 rounded-2xl bg-gradient-to-br from-rose-pop to-rose-soft flex items-center justify-center cursor-pointer hover:scale-105 transition-transform overflow-hidden"
                    onClick={() => setShowAvatarPicker(true)}
                    data-testid="avatar-btn"
                  >
                    {customer.avatar ? (
                      <img src={customer.avatar} alt="Avatar" className="w-full h-full object-cover" />
                    ) : (
                      <>
                        <User className="w-7 h-7 text-white" />
                        <div className="absolute -bottom-1 -right-1 bg-white rounded-full p-1 shadow-md">
                          <Edit2 className="w-3 h-3 text-rose-pop" />
                        </div>
                      </>
                    )}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <h2 className="font-nunito font-bold text-lg text-gray-900">
                        {customer.name || 'Sin nombre'}
                      </h2>
                      <button 
                        onClick={() => {
                          setEditName(customer.name || '');
                          setShowEditName(true);
                        }}
                        className="p-1 hover:bg-rose-faint rounded-full transition-colors"
                        data-testid="edit-name-btn"
                      >
                        <Edit2 className="w-4 h-4 text-gray-400 hover:text-rose-pop" />
                      </button>
                    </div>
                    <p className="text-sm text-gray-500 font-quicksand">{customer.email}</p>
                  </div>
                </div>
                
                {/* Stats Row */}
                <div className="grid grid-cols-2 gap-3 mb-4">
                  <div className="bg-rose-faint rounded-2xl p-4 text-center">
                    <p className="text-sm text-gray-700 font-nunito font-bold mb-2">Stickers</p>
                    <img 
                      src="https://hosting.photobucket.com/d970c25b-dfba-4164-a867-030817bbaa89/40f527ff-70b4-47b2-81b0-b9b1ab0e6db2.png" 
                      alt="sticker"
                      className="w-24 h-24 object-contain mx-auto drop-shadow-lg"
                      style={{ filter: 'drop-shadow(3px 3px 4px rgba(0,0,0,0.3))' }}
                    />
                    <p className="font-nunito font-bold text-2xl text-rose-pop mt-2">
                      {currentStickers}/10
                    </p>
                    {canConvertToCoins && (
                      <p className="text-xs text-amber-600 font-quicksand animate-pulse mt-1">¡Listo para convertir!</p>
                    )}
                  </div>
                  <div className="bg-amber-100 rounded-2xl p-4 text-center">
                    <p className="text-sm text-gray-700 font-nunito font-bold mb-2">Monedas de Oro</p>
                    <img 
                      src="https://hosting.photobucket.com/d970c25b-dfba-4164-a867-030817bbaa89/0094c644-87b7-4b8d-9dc3-4a8084bf4af5.png" 
                      alt="moneda"
                      className="w-24 h-24 object-contain mx-auto drop-shadow-lg"
                      style={{ filter: 'drop-shadow(3px 3px 4px rgba(0,0,0,0.3))' }}
                    />
                    <p className="font-nunito font-bold text-2xl text-amber-600 mt-2">{goldCoins}</p>
                  </div>
                </div>
                
                {/* Redeem Button */}
              </CardContent>
            </Card>

          </>
        ) : (
          <div data-testid="welcome-card">
            <img 
              src="https://hosting.photobucket.com/d970c25b-dfba-4164-a867-030817bbaa89/d90008f5-6638-4997-a0d3-1d70ad615038.png"
              alt="Bienvenido"
              className="w-full rounded-3xl cursor-pointer hover:scale-[1.02] transition-transform duration-300"
              style={{ border: '5px solid #FFA6A6' }}
              onClick={() => setShowAuth(true)}
              data-testid="start-btn"
            />
            
            <p className="text-center text-white font-semibold my-4 px-2" style={{ textShadow: '2px 2px 4px rgba(0,0,0,0.5)', fontSize: 'clamp(0.85rem, 3.5vw, 1.125rem)' }}>
              ¡Acumula stickers y canjéalos por premios increíbles!
            </p>
            
            <img 
              src="https://hosting.photobucket.com/d970c25b-dfba-4164-a867-030817bbaa89/ad61e329-d3ce-45da-b017-2df2f17133e1.png"
              alt="Instalar App"
              className="w-full rounded-3xl cursor-pointer hover:scale-[1.02] transition-transform duration-300"
              style={{ border: '5px solid #FFA6A6' }}
              onClick={handleInstallClick}
              data-testid="install-btn"
            />

            {/* WiFi Banner */}
            <img 
              src="https://hosting.photobucket.com/d970c25b-dfba-4164-a867-030817bbaa89/7dfd158b-1376-4ec6-9cc8-84fbe668f24f.png"
              alt="Conéctate al WiFi"
              className="w-full rounded-3xl mt-4 cursor-pointer hover:scale-[1.02] transition-transform duration-300"
              style={{ border: '5px solid #FFA6A6' }}
            />

            {/* Two column images */}
            <div className="flex gap-[10px] mt-[10px]">
              <a href="http://darkarcompany.com" target="_blank" rel="noopener noreferrer" className="w-1/2">
                <img 
                  src="https://hosting.photobucket.com/d970c25b-dfba-4164-a867-030817bbaa89/b902bb39-71a0-491d-8043-49cf9466def7.png"
                  alt="Darkar Company"
                  className="w-full aspect-square rounded-2xl cursor-pointer hover:scale-[1.02] transition-transform duration-300 object-cover"
                  style={{ border: '5px solid #FFA6A6' }}
                  data-testid="square-img-1"
                />
              </a>
              <a href="http://darkar.co" target="_blank" rel="noopener noreferrer" className="w-1/2">
                <img 
                  src="https://hosting.photobucket.com/d970c25b-dfba-4164-a867-030817bbaa89/9e733b6f-0550-4125-aa9d-479275444dc2.png"
                  alt="Darkar Co"
                  className="w-full aspect-square rounded-2xl cursor-pointer hover:scale-[1.02] transition-transform duration-300 object-cover"
                  style={{ border: '5px solid #FFA6A6' }}
                  data-testid="square-img-2"
                />
              </a>
            </div>
          </div>
        )}

        {/* Promotions Carousel */}
        <div className="mt-2">
          <PromoCarousel promotions={promotions} />
        </div>
      </main>

      {/* Auth Drawer */}
      <Drawer open={showAuth} onOpenChange={setShowAuth}>
        <DrawerContent className="bg-white rounded-t-3xl">
          <DrawerHeader className="text-center">
            <DrawerTitle className="font-nunito text-2xl text-gray-900">
              {authMode === 'login' ? 'Iniciar Sesión' : 'Crear Cuenta'}
            </DrawerTitle>
            <DrawerDescription className="font-quicksand text-gray-600">
              {authMode === 'login' ? 'Ingresa con tu correo y contraseña' : 'Regístrate para acumular visitas'}
            </DrawerDescription>
          </DrawerHeader>
          
          <div className="px-6 pb-6">
            {authMode === 'register' && (
              <div className="relative mb-4">
                <Input
                  type="text"
                  placeholder="Tu nombre"
                  value={authData.name}
                  onChange={(e) => setAuthData(prev => ({ ...prev, name: e.target.value }))}
                  className="pl-12 py-5 rounded-2xl border-2 border-rose-soft focus:border-rose-pop text-lg"
                  data-testid="auth-name-input"
                />
                <div className="absolute left-4 top-1/2 -translate-y-1/2">
                  <User className="w-5 h-5 text-rose-pop" />
                </div>
              </div>
            )}

            {authMode === 'register' && (
              <div className="relative mb-4">
                <Input
                  type="tel"
                  placeholder="Teléfono"
                  value={authData.phone}
                  onChange={(e) => setAuthData(prev => ({ ...prev, phone: e.target.value }))}
                  className="pl-12 py-5 rounded-2xl border-2 border-rose-soft focus:border-rose-pop text-lg"
                  data-testid="auth-phone-input"
                />
                <div className="absolute left-4 top-1/2 -translate-y-1/2">
                  <span className="text-rose-pop text-lg">📱</span>
                </div>
              </div>
            )}

            <div className="relative mb-4">
              <Input
                type="email"
                placeholder="Correo electrónico"
                value={authData.email}
                onChange={(e) => setAuthData(prev => ({ ...prev, email: e.target.value }))}
                className="pl-12 py-5 rounded-2xl border-2 border-rose-soft focus:border-rose-pop text-lg"
                data-testid="auth-email-input"
              />
              <div className="absolute left-4 top-1/2 -translate-y-1/2">
                <Mail className="w-5 h-5 text-rose-pop" />
              </div>
            </div>

            <div className="relative mb-4">
              <Input
                type="password"
                placeholder="Contraseña"
                value={authData.password}
                onChange={(e) => setAuthData(prev => ({ ...prev, password: e.target.value }))}
                className="pl-12 py-5 rounded-2xl border-2 border-rose-soft focus:border-rose-pop text-lg"
                data-testid="auth-password-input"
              />
              <div className="absolute left-4 top-1/2 -translate-y-1/2">
                <Lock className="w-5 h-5 text-rose-pop" />
              </div>
            </div>

            {authMode === 'register' && (
              <div className="relative mb-4">
                <Input
                  type="password"
                  placeholder="Confirmar contraseña"
                  value={authData.confirmPassword}
                  onChange={(e) => setAuthData(prev => ({ ...prev, confirmPassword: e.target.value }))}
                  className={`pl-12 py-5 rounded-2xl border-2 text-lg ${
                    authData.confirmPassword && authData.password !== authData.confirmPassword 
                      ? 'border-red-400 focus:border-red-500' 
                      : 'border-rose-soft focus:border-rose-pop'
                  }`}
                  data-testid="auth-confirm-password-input"
                />
                <div className="absolute left-4 top-1/2 -translate-y-1/2">
                  <Lock className="w-5 h-5 text-rose-pop" />
                </div>
                {authData.confirmPassword && authData.password !== authData.confirmPassword && (
                  <p className="text-red-500 text-xs mt-1 ml-2">Las contraseñas no coinciden</p>
                )}
              </div>
            )}
            
            <Button
              onClick={authMode === 'login' ? handleLogin : handleRegister}
              disabled={loading}
              className="w-full bg-rose-pop hover:bg-rose-dark text-white font-bold py-6 rounded-2xl click-squish text-lg"
              data-testid="auth-submit-btn"
            >
              {loading ? 'Cargando...' : (authMode === 'login' ? 'Entrar' : 'Registrarme')}
            </Button>
            
            {/* Forgot Password Link - Only shown on login */}
            {authMode === 'login' && (
              <button
                onClick={() => {
                  setShowAuth(false);
                  setShowForgotPassword(true);
                }}
                className="mt-3 text-center text-sm text-gray-500 font-quicksand hover:text-rose-pop hover:underline w-full"
                data-testid="forgot-password-link"
              >
                ¿Olvidaste tu contraseña?
              </button>
            )}
          </div>
          
          <DrawerFooter className="pt-0">
            <button
              onClick={() => setAuthMode(authMode === 'login' ? 'register' : 'login')}
              className="text-center text-sm text-rose-pop font-quicksand hover:underline"
              data-testid="auth-toggle-btn"
            >
              {authMode === 'login' ? '¿No tienes cuenta? Regístrate' : '¿Ya tienes cuenta? Inicia sesión'}
            </button>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>

      {/* Redemption Code Dialog */}
      <Dialog open={showRedemptionCode} onOpenChange={(open) => !open && handleCancelRedemption()}>
        <DialogContent className="bg-white rounded-3xl border-4 border-green-400 max-w-sm mx-auto">
          <DialogHeader>
            <DialogTitle className="font-nunito text-xl text-gray-900 text-center">
              ¡Muestra este código en caja!
            </DialogTitle>
            <DialogDescription className="text-center text-gray-500">
              {redemptionCode?.rewardName}
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-6">
            <div className="bg-green-50 rounded-2xl p-6 text-center">
              <p className="text-6xl font-bold text-green-600 tracking-[0.3em] font-mono">
                {redemptionCode?.code}
              </p>
            </div>
            
            <div className="mt-4 text-center">
              <p className="text-sm text-gray-500">
                Costo: {redemptionCode?.cost} {redemptionCode?.currencyType === 'stickers' ? 'stickers' : 'monedas'}
              </p>
              {codeExpiry && <CountdownTimer expiry={codeExpiry} onExpire={handleCancelRedemption} />}
            </div>
          </div>
          
          <Button
            onClick={handleCancelRedemption}
            variant="outline"
            className="w-full py-5 rounded-2xl border-2 border-gray-300"
          >
            Cancelar
          </Button>
        </DialogContent>
      </Dialog>

      {/* Avatar Picker Dialog */}
      <Dialog open={showAvatarPicker} onOpenChange={setShowAvatarPicker}>
        <DialogContent className="bg-white rounded-3xl border-2 border-rose-soft max-w-sm mx-auto">
          <DialogHeader>
            <DialogTitle className="font-nunito text-xl text-gray-900 text-center">Elige tu Avatar</DialogTitle>
            <DialogDescription className="text-center text-gray-500">
              Selecciona una imagen para tu perfil
            </DialogDescription>
          </DialogHeader>
          
          <div className="grid grid-cols-4 gap-3 py-4">
            {avatarOptions.map((avatar, index) => (
              <button
                key={index}
                onClick={() => handleSelectAvatar(avatar)}
                className={`w-16 h-16 rounded-xl overflow-hidden border-2 transition-all hover:scale-110 ${
                  customer?.avatar === avatar ? 'border-rose-pop ring-2 ring-rose-300' : 'border-gray-200 hover:border-rose-300'
                }`}
                data-testid={`avatar-option-${index}`}
              >
                <img src={avatar} alt={`Avatar ${index + 1}`} className="w-full h-full object-cover" />
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={showEditName} onOpenChange={setShowEditName}>
        <DialogContent className="bg-white rounded-3xl border-2 border-rose-soft max-w-sm mx-auto">
          <DialogHeader>
            <DialogTitle className="font-nunito text-xl text-gray-900 text-center">Editar Nombre</DialogTitle>
            <DialogDescription className="text-center text-gray-500">
              Ingresa tu nombre para personalizar tu cuenta
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            <Input
              type="text"
              placeholder="Tu nombre"
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
              className="py-5 rounded-2xl border-2 border-rose-soft focus:border-rose-pop"
              data-testid="edit-name-input"
            />
          </div>
          
          <Button
            onClick={handleUpdateName}
            disabled={loading}
            className="w-full bg-rose-pop hover:bg-rose-dark text-white font-bold py-5 rounded-2xl click-squish"
            data-testid="save-name-btn"
          >
            {loading ? 'Guardando...' : 'Guardar'}
          </Button>
        </DialogContent>
      </Dialog>

      {/* Reward Dialog */}
      <Dialog open={showReward} onOpenChange={setShowReward}>
        <DialogContent className="bg-white rounded-3xl border-2 border-rose-400 max-w-sm mx-auto max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-nunito text-2xl text-gray-900 text-center flex items-center justify-center gap-2">
              <Gift className="w-7 h-7 text-rose-500" />
              ¡Canjear Premios!
            </DialogTitle>
            <DialogDescription className="font-quicksand text-gray-600 text-center">
              Tienes <span className="font-bold text-rose-500">🎟️ {currentStickers} stickers</span> y <span className="font-bold text-amber-600">🪙 {goldCoins} monedas</span>
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Convert to Gold Coin Option - Always visible */}
            <div className="mb-4">
              <p className="text-xs text-gray-500 mb-2 font-quicksand">MONEDA DE ORO</p>
              <Card 
                className={`border-2 rounded-2xl transition-all ${canConvertToCoins ? 'border-blue-300 hover:border-blue-500 cursor-pointer card-interactive bg-blue-50' : 'border-gray-200 bg-gray-50 opacity-60'}`}
                onClick={canConvertToCoins ? handleConvertStickers : undefined}
                data-testid="convert-to-coin-btn"
              >
                <CardContent className="p-4 flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center bg-blue-200">
                    <img 
                      src="https://hosting.photobucket.com/d970c25b-dfba-4164-a867-030817bbaa89/005fa523-d211-4211-ba7f-a45ade3246f1.png" 
                      alt="moneda"
                      className="w-8 h-8 object-contain"
                    />
                  </div>
                  <div className="flex-1">
                    <h4 className="font-nunito font-bold text-gray-900">Moneda de Oro</h4>
                    <p className="text-sm text-gray-500 font-quicksand">
                      {canConvertToCoins ? 'Cambia 10 stickers por 1 moneda de oro' : `Te faltan ${10 - currentStickers} stickers`}
                    </p>
                  </div>
                  <div className="text-sm text-amber-600 font-bold">
                    10 🎟️
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Sticker Rewards - rewards that cost stickers */}
            {rewards.filter(r => r.currency_type === 'stickers' && (r.cost || 1) <= currentStickers).length > 0 && (
              <div>
                <p className="text-xs text-gray-500 mb-2 font-quicksand">PREMIOS POR STICKERS</p>
                {rewards.filter(r => r.currency_type === 'stickers' && (r.cost || 1) <= currentStickers).map((reward) => (
                  <Card 
                    key={reward.id}
                    className="border-2 border-rose-200 hover:border-rose-400 rounded-2xl cursor-pointer transition-all card-interactive mb-2"
                    onClick={() => handleRedeemReward(reward)}
                    data-testid={`reward-option-${reward.id}`}
                  >
                    <CardContent className="p-4 flex items-center gap-4">
                      <div className="w-12 h-12 rounded-xl flex items-center justify-center bg-rose-100">
                        <Gift className="w-6 h-6 text-rose-500" />
                      </div>
                      <div className="flex-1">
                        <h4 className="font-nunito font-bold text-gray-900">{reward.name}</h4>
                        <p className="text-sm text-gray-500 font-quicksand">
                          {reward.description || reward.value}
                        </p>
                      </div>
                      <div className="text-sm text-rose-500 font-bold">
                        {reward.cost || 1} 🎟️
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}

            {/* Coin Rewards - rewards that cost gold coins */}
            {rewards.filter(r => (r.currency_type === 'coins' || !r.currency_type) && (r.cost || 1) <= goldCoins).length > 0 && (
              <div>
                <p className="text-xs text-gray-500 mb-2 font-quicksand">PREMIOS POR MONEDAS</p>
                {rewards.filter(r => (r.currency_type === 'coins' || !r.currency_type) && (r.cost || 1) <= goldCoins).map((reward) => (
                  <Card 
                    key={reward.id}
                    className="border-2 border-amber-200 hover:border-amber-400 rounded-2xl cursor-pointer transition-all card-interactive mb-2"
                    onClick={() => handleRedeemReward(reward)}
                    data-testid={`reward-option-${reward.id}`}
                  >
                    <CardContent className="p-4 flex items-center gap-4">
                      <div className="w-12 h-12 rounded-xl flex items-center justify-center bg-blue-100">
                        <Gift className="w-6 h-6 text-blue-500" />
                      </div>
                      <div className="flex-1">
                        <h4 className="font-nunito font-bold text-gray-900">{reward.name}</h4>
                        <p className="text-sm text-gray-500 font-quicksand">
                          {reward.description || reward.value}
                        </p>
                      </div>
                      <div className="flex items-center gap-1 text-amber-600 font-bold">
                        <img src="https://hosting.photobucket.com/d970c25b-dfba-4164-a867-030817bbaa89/005fa523-d211-4211-ba7f-a45ade3246f1.png" alt="moneda" className="w-5 h-5 inline" /> {reward.cost || 1}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}

            {/* All available rewards (not enough currency) */}
            {(() => {
              const availableStickerRewards = rewards.filter(r => r.currency_type === 'stickers' && (r.cost || 1) > currentStickers);
              const availableCoinRewards = rewards.filter(r => (r.currency_type === 'coins' || !r.currency_type) && (r.cost || 1) > goldCoins);
              const hasUnaffordable = availableStickerRewards.length > 0 || availableCoinRewards.length > 0;
              
              if (!hasUnaffordable) return null;
              
              return (
                <div>
                  <p className="text-xs text-gray-400 mb-2 font-quicksand">PRÓXIMOS PREMIOS</p>
                  {availableStickerRewards.map((reward) => (
                    <Card 
                      key={reward.id}
                      className="border-2 border-gray-200 rounded-2xl mb-2 opacity-60"
                      data-testid={`reward-locked-${reward.id}`}
                    >
                      <CardContent className="p-4 flex items-center gap-4">
                        <div className="w-12 h-12 rounded-xl flex items-center justify-center bg-gray-100">
                          <Gift className="w-6 h-6 text-gray-400" />
                        </div>
                        <div className="flex-1">
                          <h4 className="font-nunito font-bold text-gray-600">{reward.name}</h4>
                          <p className="text-sm text-gray-400 font-quicksand">
                            Te faltan {(reward.cost || 1) - currentStickers} stickers
                          </p>
                        </div>
                        <div className="text-sm text-gray-400 font-bold">
                          {reward.cost || 1} 🎟️
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                  {availableCoinRewards.map((reward) => (
                    <Card 
                      key={reward.id}
                      className="border-2 border-gray-200 rounded-2xl mb-2 opacity-60"
                      data-testid={`reward-locked-${reward.id}`}
                    >
                      <CardContent className="p-4 flex items-center gap-4">
                        <div className="w-12 h-12 rounded-xl flex items-center justify-center bg-gray-100">
                          <Gift className="w-6 h-6 text-gray-400" />
                        </div>
                        <div className="flex-1">
                          <h4 className="font-nunito font-bold text-gray-600">{reward.name}</h4>
                          <p className="text-sm text-gray-400 font-quicksand">
                            Te faltan {(reward.cost || 1) - goldCoins} monedas
                          </p>
                        </div>
                        <div className="flex items-center gap-1 text-gray-400 font-bold">
                          <img src="https://hosting.photobucket.com/d970c25b-dfba-4164-a867-030817bbaa89/005fa523-d211-4211-ba7f-a45ade3246f1.png" alt="moneda" className="w-5 h-5 inline" /> {reward.cost || 1}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              );
            })()}

            {/* No rewards configured message */}
            {rewards.length === 0 && !canConvertToCoins && (
              <p className="text-center text-gray-500 py-8">
                No hay premios configurados todavía
              </p>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Footer */}
      <footer className="text-center py-2 text-sm text-white font-semibold" style={{ textShadow: '2px 2px 4px rgba(0,0,0,0.5)' }}>
        Copyright © {new Date().getFullYear()} Darkar Company Studios, LLC.
      </footer>

      {/* Forgot Password Dialog */}
      <Dialog open={showForgotPassword} onOpenChange={setShowForgotPassword}>
        <DialogContent className="bg-white rounded-3xl border-2 border-rose-soft max-w-sm mx-auto">
          <DialogHeader>
            <DialogTitle className="font-nunito text-xl text-gray-900 text-center">
              Recuperar Contraseña
            </DialogTitle>
            <DialogDescription className="text-center text-gray-500">
              Ingresa tu correo electrónico y te enviaremos instrucciones para restablecer tu contraseña.
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            <div className="relative mb-4">
              <Input
                type="email"
                placeholder="Tu correo electrónico"
                value={forgotEmail}
                onChange={(e) => setForgotEmail(e.target.value)}
                className="pl-12 py-5 rounded-2xl border-2 border-rose-soft focus:border-rose-pop text-lg"
                data-testid="forgot-email-input"
              />
              <div className="absolute left-4 top-1/2 -translate-y-1/2">
                <Mail className="w-5 h-5 text-rose-pop" />
              </div>
            </div>
            
            <Button
              onClick={handleForgotPassword}
              disabled={loading}
              className="w-full bg-rose-pop hover:bg-rose-dark text-white font-bold py-5 rounded-2xl click-squish"
              data-testid="send-reset-btn"
            >
              {loading ? 'Enviando...' : 'Enviar Instrucciones'}
            </Button>
            
            <button
              onClick={() => {
                setShowForgotPassword(false);
                setForgotEmail('');
                setShowAuth(true);
                setAuthMode('login');
              }}
              className="mt-3 text-center text-sm text-gray-500 font-quicksand hover:text-rose-pop hover:underline w-full"
              data-testid="back-to-login-link"
            >
              Volver al inicio de sesión
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Reset Password Dialog */}
      <Dialog open={showResetPassword} onOpenChange={(open) => {
        if (!open) {
          setShowResetPassword(false);
          setResetToken('');
          setResetData({ newPassword: '', confirmPassword: '' });
          setResetEmail('');
        }
      }}>
        <DialogContent className="bg-white rounded-3xl border-2 border-rose-soft max-w-sm mx-auto">
          <DialogHeader>
            <DialogTitle className="font-nunito text-xl text-gray-900 text-center">
              Nueva Contraseña
            </DialogTitle>
            <DialogDescription className="text-center text-gray-500">
              {resetEmail && <span>Cuenta: <strong>{resetEmail}</strong><br/></span>}
              Ingresa tu nueva contraseña.
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            <div className="relative mb-4">
              <Input
                type="password"
                placeholder="Nueva contraseña"
                value={resetData.newPassword}
                onChange={(e) => setResetData(prev => ({ ...prev, newPassword: e.target.value }))}
                className="pl-12 py-5 rounded-2xl border-2 border-rose-soft focus:border-rose-pop text-lg"
                data-testid="new-password-input"
              />
              <div className="absolute left-4 top-1/2 -translate-y-1/2">
                <Lock className="w-5 h-5 text-rose-pop" />
              </div>
            </div>
            
            <div className="relative mb-4">
              <Input
                type="password"
                placeholder="Confirmar contraseña"
                value={resetData.confirmPassword}
                onChange={(e) => setResetData(prev => ({ ...prev, confirmPassword: e.target.value }))}
                className={`pl-12 py-5 rounded-2xl border-2 text-lg ${
                  resetData.confirmPassword && resetData.newPassword !== resetData.confirmPassword 
                    ? 'border-red-400 focus:border-red-500' 
                    : 'border-rose-soft focus:border-rose-pop'
                }`}
                data-testid="confirm-new-password-input"
              />
              <div className="absolute left-4 top-1/2 -translate-y-1/2">
                <Lock className="w-5 h-5 text-rose-pop" />
              </div>
              {resetData.confirmPassword && resetData.newPassword !== resetData.confirmPassword && (
                <p className="text-red-500 text-xs mt-1 ml-2">Las contraseñas no coinciden</p>
              )}
            </div>
            
            <Button
              onClick={handleResetPassword}
              disabled={loading}
              className="w-full bg-rose-pop hover:bg-rose-dark text-white font-bold py-5 rounded-2xl click-squish"
              data-testid="reset-password-btn"
            >
              {loading ? 'Actualizando...' : 'Actualizar Contraseña'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CustomerApp;
